import React from 'react';
import { GridContainer, Column, Row, useScreenClass } from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { ModTarget } from '@vp/mod-father';
import { CartItems } from '../cartitems/CartItems';
import { CouponPanel } from '../coupon/CouponPanel';
import CartPriceSummary from '../pricing/CartPriceSummary';
import CheckoutButton from './layoutComponents/CheckoutButton';
import SignInAndCheckoutButton from './layoutComponents/SignInAndCheckoutButton';
import { ModWrapperWithPadding } from '../uxMods/ModWrapperWithPadding';
import { CartMainHeading } from './layoutComponents/CartMainHeading';
import { pricingSelectors } from '../../state/pricing';
import { PromotionProgressWrapper } from '../coupon/PromotionProgressWrapper';
import { PayPalMessageDisplay } from '../pricing/PayPalMessageDisplay';
import { PayPalPaymentAcceleratorWrapper } from '../pricing/PayPalPaymentAcceleratorWrapper';
import { FreeShippingProgressBarWrapper } from '../FreeShippingProgressBarWrapper';
import useFeature from '../../hooks/useFeature';
import useExperiment from '../../hooks/useExperiment';
import { PAYPAL_PAYMENT_ACCELERATOR } from '../../optimizely/optimizelyExperiments';
import { cartItemSelectors } from '../../state/cartItems';

export function CartLayout({
  displaySignInAndCheckoutButton,
  checkoutDisabled,
  checkoutLinkDisabled,
}) {
  const screenClass = useScreenClass();
  const [cartMessages] = useTranslations(['cartMessages']);

  const shouldDisplayPayPalMessage = useFeature('showPayPalPayLaterMessaging');

  const zeroDollarCart = useSelector(
    state => pricingSelectors.selectDiscountedTotal(state.pricing).gross === 0
  );
  const cartContainsSubscription = useSelector(state =>
    cartItemSelectors.selectCartContainsSubscription(state)
  );
  const showPayPalPaymentAccelerator = useExperiment(
    PAYPAL_PAYMENT_ACCELERATOR.experimentKey,
    PAYPAL_PAYMENT_ACCELERATOR.activeVariant,
    !zeroDollarCart && !cartContainsSubscription
  );

  const shouldRenderFreeShippingProgressBar = useFeature(
    'enableFreeShippingOverThresholdProgressBar'
  );

  return (
    <GridContainer>
      {screenClass === 'xs' && <CartMainHeading />}
      <Row>
        <Column span={5} push={7}>
          <Row mt="5">
            <Column
              span={12}
              px={{ sm: '6' }}
              py={{ xs: '3', sm: '6' }}
              mb="5"
              backgroundColor="strong"
            >
              <Row marginBottom="5">
                <Column span={12}>
                  <CartPriceSummary />
                </Column>
              </Row>
              <Row marginBottom="5">
                <Column span={12}>
                  <CouponPanel />
                </Column>
              </Row>
              {displaySignInAndCheckoutButton ? (
                <Row marginBottom="5">
                  <Column span={12}>
                    <SignInAndCheckoutButton
                      buttonText={cartMessages.signInAndCheckout}
                      disabled={!!checkoutLinkDisabled}
                    />
                  </Column>
                </Row>
              ) : null}
              <Row>
                <Column span={12}>
                  <CheckoutButton
                    trackingCtaValue={
                      displaySignInAndCheckoutButton
                        ? 'checkoutAsGuest'
                        : 'checkout'
                    }
                    buttonText={
                      displaySignInAndCheckoutButton
                        ? cartMessages.checkoutAsGuest
                        : cartMessages.checkout
                    }
                    disabled={!!checkoutLinkDisabled}
                    skin={
                      /**
                       * If "sign in and checkout" button is present, that
                       * button gains prominence so the skin on this checkout
                       * button is switched to secondary. If "sign in and
                       * checkout" is not present, skin this button as primary
                       */
                      displaySignInAndCheckoutButton ? 'secondary' : 'primary'
                    }
                  />
                </Column>
              </Row>
              {showPayPalPaymentAccelerator && (
                <Row mt={5}>
                  <Column span={12}>
                    <PayPalPaymentAcceleratorWrapper
                      hideButton={checkoutDisabled}
                      disableButton={checkoutLinkDisabled}
                    />
                  </Column>
                </Row>
              )}
            </Column>
          </Row>

          {shouldDisplayPayPalMessage && (
            <Row marginTop="5">
              <Column span={12}>
                <PayPalMessageDisplay />
              </Column>
            </Row>
          )}
          <Row>
            <Column span={12}>
              <PromotionProgressWrapper />
            </Column>
          </Row>
          {shouldRenderFreeShippingProgressBar && (
            <Row>
              <Column span={12}>
                <FreeShippingProgressBarWrapper />
              </Column>
            </Row>
          )}
          <Row mt="5">
            <Column span={12}>
              <ModTarget
                name="belowPricingSummary"
                hostModWrapper={ModWrapperWithPadding}
              />
            </Column>
          </Row>
        </Column>
        <Column span={6} pull={5}>
          {screenClass !== 'xs' && <CartMainHeading />}
          <CartItems />
        </Column>
      </Row>
    </GridContainer>
  );
}

CartLayout.propTypes = {
  displaySignInAndCheckoutButton: PropTypes.bool.isRequired,
  checkoutDisabled: PropTypes.bool.isRequired,
  checkoutLinkDisabled: PropTypes.bool.isRequired,
};
